import * as React from 'react';
import { ReactNode, useCallback } from 'react';
import {
  createInstance,
  type Resource,
  type i18n as I18nInstance,
} from 'i18next';
import {
  I18nextProvider,
  initReactI18next,
  useTranslation as useI18nextTranslation,
} from 'react-i18next';
import {
  DEFAULT_I18N_FALLBACK_LOCALE,
  DEFAULT_I18N_LOCALES,
  DEFAULT_I18N_LOCALE_NAMES,
  DEFAULT_I18N_STORAGE_KEY,
  type DefaultI18nLocale,
} from './config';

type TranslationDictionary = Record<string, unknown>;

export type SupportedLocale = DefaultI18nLocale | (string & {});

export type CreateI18nOptions = {
  namespace: string;
  resources: Partial<Record<SupportedLocale, TranslationDictionary>>;
  fallbackLng?: SupportedLocale;
  supportedLngs?: readonly SupportedLocale[];
  lng?: SupportedLocale;
  storageKey?: string;
  debug?: boolean;
  useSuspense?: boolean;
};

export type I18nProviderProps = {
  children: ReactNode;
  i18n: I18nInstance;
  fallback?: ReactNode;
};

const resolveFallbackLocale = (
  fallbackLng: I18nInstance['options']['fallbackLng'],
  defaultFallbackLocale: string = DEFAULT_I18N_FALLBACK_LOCALE
): string => {
  if (typeof fallbackLng === 'string') {
    return fallbackLng;
  }

  if (Array.isArray(fallbackLng)) {
    return fallbackLng[0] ?? defaultFallbackLocale;
  }

  return defaultFallbackLocale;
};

export const normalizeLocale = (
  locale?: string,
  supportedLocales: readonly string[] = DEFAULT_I18N_LOCALES,
  fallbackLocale: string = DEFAULT_I18N_FALLBACK_LOCALE
): string => {
  const normalizedLocale = locale?.trim();

  if (!normalizedLocale) {
    return fallbackLocale;
  }

  const exactMatch = supportedLocales.find(
    (supportedLocale) =>
      supportedLocale.toLowerCase() === normalizedLocale.toLowerCase()
  );

  if (exactMatch) {
    return exactMatch;
  }

  const localePrefix = normalizedLocale.split('-')[0]?.toLowerCase();
  const prefixMatch = supportedLocales.find((supportedLocale) =>
    supportedLocale.toLowerCase().startsWith(localePrefix)
  );

  return prefixMatch ?? fallbackLocale;
};

export const isSupportedLocale = (
  locale: string,
  supportedLocales: readonly string[] = DEFAULT_I18N_LOCALES
): boolean =>
  supportedLocales.some(
    (supportedLocale) => supportedLocale.toLowerCase() === locale.toLowerCase()
  );

export const getStoredLocale = (
  supportedLocales: readonly string[] = DEFAULT_I18N_LOCALES,
  storageKey: string = DEFAULT_I18N_STORAGE_KEY
): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedLocale = window.localStorage.getItem(storageKey);

  if (!storedLocale || !isSupportedLocale(storedLocale, supportedLocales)) {
    return null;
  }

  return normalizeLocale(storedLocale, supportedLocales);
};

export const storeLocale = (
  locale: string,
  storageKey: string = DEFAULT_I18N_STORAGE_KEY
): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(storageKey, locale);
  }
};

export const getBrowserLocale = (
  supportedLocales: readonly string[] = DEFAULT_I18N_LOCALES,
  fallbackLocale: string = DEFAULT_I18N_FALLBACK_LOCALE
): string => {
  if (typeof window === 'undefined') {
    return fallbackLocale;
  }

  return normalizeLocale(window.navigator.language, supportedLocales, fallbackLocale);
};

export const getInitialLocale = (
  supportedLocales: readonly string[] = DEFAULT_I18N_LOCALES,
  fallbackLocale: string = DEFAULT_I18N_FALLBACK_LOCALE,
  storageKey: string = DEFAULT_I18N_STORAGE_KEY
): string =>
  getStoredLocale(supportedLocales, storageKey) ??
  getBrowserLocale(supportedLocales, fallbackLocale) ??
  fallbackLocale;

export const createNamespacedResources = (
  namespace: string,
  resources: Partial<Record<SupportedLocale, TranslationDictionary>>
): Resource =>
  Object.entries(resources).reduce<Resource>((acc, [locale, translations]) => {
    if (!translations) {
      return acc;
    }

    acc[locale] = {
      [namespace]: translations,
    };

    return acc;
  }, {});

export const createI18n = ({
  namespace,
  resources,
  fallbackLng = DEFAULT_I18N_FALLBACK_LOCALE,
  supportedLngs = DEFAULT_I18N_LOCALES,
  lng,
  storageKey = DEFAULT_I18N_STORAGE_KEY,
  debug = false,
  useSuspense = true,
}: CreateI18nOptions): I18nInstance => {
  const instance = createInstance();
  const initialLocale = normalizeLocale(
    lng ?? getInitialLocale(supportedLngs, fallbackLng, storageKey),
    supportedLngs,
    fallbackLng
  );

  void instance.use(initReactI18next).init({
    lng: initialLocale,
    initAsync: false,
    fallbackLng,
    supportedLngs: [...supportedLngs],
    defaultNS: namespace,
    ns: [namespace],
    resources: createNamespacedResources(namespace, resources),
    keySeparator: '.',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense,
    },
    debug,
    returnNull: false,
    returnEmptyString: false,
  });

  return instance;
};

export function useI18nTranslation(namespace?: string) {
  return useI18nextTranslation(namespace, {
    useSuspense: true,
  });
}

export function useCurrentLocale(): string {
  const { i18n } = useI18nextTranslation();
  return i18n.language;
}

export function useChangeLocale(storageKey: string = DEFAULT_I18N_STORAGE_KEY) {
  const { i18n } = useI18nextTranslation();

  return useCallback(
    async (locale: string) => {
      const normalizedLocale = normalizeLocale(
        locale,
        Array.isArray(i18n.options.supportedLngs)
          ? i18n.options.supportedLngs
          : undefined,
        resolveFallbackLocale(i18n.options.fallbackLng)
      );
      storeLocale(normalizedLocale, storageKey);
      await i18n.changeLanguage(normalizedLocale);
    },
    [i18n, storageKey]
  );
}

export const getAvailableLocales = (
  supportedLocales: readonly SupportedLocale[] = DEFAULT_I18N_LOCALES,
  localeNames: Partial<Record<SupportedLocale, string>> = DEFAULT_I18N_LOCALE_NAMES
) =>
  supportedLocales.map((locale) => ({
    code: locale,
    name: localeNames[locale] ?? locale,
  }));

export function usePrefixedTranslation(prefix: string, namespace?: string) {
  const { t } = useI18nTranslation(namespace);

  return useCallback(
    (key: string, options?: Record<string, unknown>) => t(`${prefix}.${key}`, options),
    [prefix, t]
  );
}

export function AppI18nProvider({ children, i18n, fallback = null }: I18nProviderProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <React.Suspense fallback={fallback}>{children}</React.Suspense>
    </I18nextProvider>
  );
}
